import { useCallback, useEffect, useMemo, useState } from "react";
import { normalizeSignatureList, findSignatureById } from "../utils/signatureUtils";
import { loadSignatureSettings, createSignature, updateSignature, deleteSignature, setDefaultSignature } from "../api/settingsApi";
import { AUTH_STORAGE_KEY } from "../socket";

export default function useSignatures(auth, authHeaders) {
  const [signatureList, setSignatureList] = useState([]);
  const [defaultSignatureId, setDefaultSignatureId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [editable, setEditable] = useState(true);

  const updateStoredSignatureData = useCallback((payload) => {
    if (!payload || typeof payload !== "object") return;
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return;
      if (!parsed.user || typeof parsed.user !== "object") return;

      // Only update stored auth when it belongs to the same user as `auth.user`
      const storedEmail = parsed.user.email || parsed.user.username || "";
      const currentEmail = auth?.user?.email || auth?.user?.username || "";
      if (!storedEmail || !currentEmail || String(storedEmail).toLowerCase() !== String(currentEmail).toLowerCase()) {
        return;
      }

      parsed.user = {
        ...parsed.user,
        ...payload,
        ...(typeof payload.editable === "boolean" ? { editable: payload.editable } : {}),
      };

      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(parsed));
    } catch {}
  }, [auth]);

  const applyPayload = useCallback((payload) => {
    const list = normalizeSignatureList(payload?.signatures, payload?.signature);
    const fallbackRecord =
      findSignatureById(list, payload?.defaultSignatureId) ||
      list.find((entry) => entry.isDefault) ||
      list[0] ||
      null;

    const fallbackId = fallbackRecord ? fallbackRecord.id : null;

    setSignatureList(list);
    setDefaultSignatureId(fallbackId);
    setEditable(typeof payload.editable === "boolean" ? payload.editable : true);

    updateStoredSignatureData({
      signature: fallbackRecord?.content || "",
      signatures: list,
      defaultSignatureId: fallbackId,
      ...(typeof payload.editable === "boolean" ? { editable: payload.editable } : {}),
    });
  }, [updateStoredSignatureData]);

  useEffect(() => {
    if (!auth?.user) return;
    applyPayload({
      signatures: auth.user.signatures,
      defaultSignatureId: auth.user.defaultSignatureId,
      signature: typeof auth.user.signature === "string" ? auth.user.signature : "",
      editable: typeof auth.user.editable === "boolean" ? auth.user.editable : undefined,
    });
  }, [applyPayload, auth?.user]);

  const refresh = useCallback(async () => {
    if (!authHeaders) return;
    setLoading(true);
    setMessage("");
    try {
      const data = await loadSignatureSettings(authHeaders);
      applyPayload(data || {});
    } catch (err) {
      const error = err.response?.data?.error || "Failed to load settings";
      setMessageType("error");
      setMessage(error);
    } finally {
      setLoading(false);
    }
  }, [applyPayload, authHeaders]);

  const saveSignature = useCallback(
    async (signatureId, payload) => {
      if (!authHeaders) return;
      setMessage("");
      setMessageType("info");
      try {
        const data = await updateSignature(signatureId, payload, authHeaders);
        applyPayload(data || {});
        setMessageType("success");
        setMessage("Signature saved successfully");
      } catch (err) {
        const error = err.response?.data?.error || "Failed to update signature";
        setMessageType("error");
        setMessage(error);
      }
    },
    [applyPayload, authHeaders]
  );

  const removeSignature = useCallback(
    async (signatureId) => {
      if (!authHeaders) return;
      setMessage("");
      setMessageType("info");
      try {
        const data = await deleteSignature(signatureId, authHeaders);
        applyPayload(data || {});
        setMessageType("success");
        setMessage("Signature removed");
      } catch (err) {
        const error = err.response?.data?.error || "Failed to delete signature";
        setMessageType("error");
        setMessage(error);
      }
    },
    [applyPayload, authHeaders]
  );

  const addSignature = useCallback(
    async (payload) => {
      if (!authHeaders) return;
      setMessage("");
      setMessageType("info");
      try {
        const data = await createSignature(payload, authHeaders);
        applyPayload(data || {});
        setMessageType("success");
        setMessage("Signature added");
      } catch (err) {
        const error = err.response?.data?.error || "Failed to add signature";
        setMessageType("error");
        setMessage(error);
      }
    },
    [applyPayload, authHeaders]
  );

  const makeDefault = useCallback(
    async (signatureId) => {
      if (!authHeaders) return;
      setMessage("");
      setMessageType("info");
      try {
        const data = await setDefaultSignature(signatureId, authHeaders);
        applyPayload(data || {});
        setMessageType("success");
        setMessage("Default signature updated");
      } catch (err) {
        const error = err.response?.data?.error || "Failed to update default signature";
        setMessageType("error");
        setMessage(error);
      }
    },
    [applyPayload, authHeaders]
  );

  const signatureMap = useMemo(() => {
    return signatureList.reduce((acc, signature) => {
      acc[String(signature.id)] = signature;
      return acc;
    }, {});
  }, [signatureList]);

  return {
    signatureList,
    defaultSignatureId,
    loading,
    message,
    messageType,
    editable,
    refresh,
    addSignature,
    saveSignature,
    removeSignature,
    makeDefault,
    signatureMap,
  };
}
